import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import QuoteWidget from '@/components/QuoteWidget';
import { CheckCircle2, ArrowRight, Shield, Clock, Users } from 'lucide-react';

const ComparePage = () => {
  const { t } = useLanguage();

  const benefits = [
    {
      icon: Clock,
      title: "Save Time",
      description: "Compare quotes from 13+ carriers in minutes, not weeks"
    },
    {
      icon: Users,
      title: "Unbiased Results",
      description: "See all options side-by-side without sales pressure"
    },
    {
      icon: Shield,
      title: "Secure Process",
      description: "Your information is encrypted and never sold"
    }
  ];

  const steps = [
    {
      number: "01",
      title: "Enter Your Details",
      description: "Provide basic information about your coverage needs and health profile"
    },
    {
      number: "02",
      title: "Compare Quotes",
      description: "Instantly see competitive quotes from top-rated A+ carriers"
    },
    {
      number: "03",
      title: "Connect with Experts",
      description: "Speak with our specialists to finalize the best policy for your needs"
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="compare-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Left Content */}
            <div>
              <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
                Instant Quote Comparison
              </span>
              <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
                Compare Life Insurance Quotes
              </h1>
              <p className="text-slate-400 text-lg mb-8 leading-relaxed">
                Access real-time quotes from America's most financially stable insurance carriers. Our comparison tool connects you directly with A+ and A++ rated insurers.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  "No obligation - quotes are free and confidential",
                  "See rates from 13+ top-rated carriers instantly",
                  "Personalized recommendations from licensed specialists",
                  "Premium financing options available for qualified clients"
                ].map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C5A059] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - Quote Widget */}
            <div>
              <QuoteWidget />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">
              Why Use Our Comparison Tool?
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Shopping for life insurance directly with carriers can be time-consuming and biased. Our marketplace approach gives you the full picture.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-slate-900/50 border border-slate-800 p-8 text-center hover:border-[#C5A059]/30 transition-colors"
                >
                  <div className="w-14 h-14 rounded-full bg-[#C5A059]/10 flex items-center justify-center mx-auto mb-6">
                    <Icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">{benefit.title}</h3>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400">
              Get started in three simple steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-6xl font-serif text-[#C5A059]/20 mb-4">
                  {step.number}
                </div>
                <h3 className="text-white font-semibold text-xl mb-3">{step.title}</h3>
                <p className="text-slate-400 leading-relaxed">{step.description}</p>
                {index < steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-700" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparePage;
